import type { KOTPrintPayload } from "@/types/orders";

export interface KOTPrintJob {
  printer_id: string;
  data: string;
  label: string;
  /** Copies of the KOT (Kitchen settings); the print service repeats the job. */
  copies?: number;
  /** The KOTs this job prints (reported to POST /kot/printed once the print service takes it). */
  kot_ids: string[];
}

/**
 * KOT print jobs for a confirm response.
 *
 * With kitchen stations the API returns one `kot_prints[]` entry per
 * station, each carrying that station's printer; those go to their own
 * printer, falling back to the outlet's default KOT printer. Without
 * stations only the combined `kot_escpos_data` is sent, which goes to the
 * default KOT printer.
 */
export function kotPrintJobs(
  order: {
    kot_escpos_data?: string | null;
    kot_prints?: KOTPrintPayload[] | null;
    kot_copies?: number | null;
    kot_ids?: string[] | null;
  },
  defaultPrinterId: string | null | undefined,
): KOTPrintJob[] {
  const copies = order.kot_copies && order.kot_copies > 1 ? { copies: order.kot_copies } : {};
  const stationPrints = order.kot_prints ?? [];
  if (stationPrints.length > 0) {
    const jobs: KOTPrintJob[] = [];
    for (const p of stationPrints) {
      const printerId = p.printer_id || defaultPrinterId;
      if (!printerId || !p.escpos_data) continue;
      jobs.push({
        printer_id: printerId,
        data: p.escpos_data,
        label: p.station_name ? `KOT · ${p.station_name}` : "KOT",
        ...copies,
        kot_ids: p.kot_id ? [p.kot_id] : [],
      });
    }
    return jobs;
  }
  if (order.kot_escpos_data && defaultPrinterId) {
    return [
      {
        printer_id: defaultPrinterId,
        data: order.kot_escpos_data,
        label: "KOT",
        ...copies,
        kot_ids: order.kot_ids ?? [],
      },
    ];
  }
  return [];
}

/**
 * Cancel slips from PATCH /orders/{id}/items: a sent line voided or reduced
 * prints a slip at its station, routed like the KOT (station printer, else
 * the default KOT printer).
 */
export function cancelSlipJobs(
  order: { cancel_slips?: KOTPrintPayload[] | null },
  defaultPrinterId: string | null | undefined,
): KOTPrintJob[] {
  const jobs: KOTPrintJob[] = [];
  for (const p of order.cancel_slips ?? []) {
    const printerId = p.printer_id || defaultPrinterId;
    if (!printerId || !p.escpos_data) continue;
    jobs.push({
      printer_id: printerId,
      data: p.escpos_data,
      label: p.station_name ? `Cancel slip · ${p.station_name}` : "Cancel slip",
      // Not a print of the KOT: never reported to POST /kot/printed.
      kot_ids: [],
    });
  }
  return jobs;
}

/**
 * Items not sent to the kitchen yet. Print Bill sends them first: the server
 * refuses a bill (409 ITEMS_NOT_SENT) while any item is new.
 */
export function hasUnsentItems(cartItems: Array<{ item_status?: string | null }>): boolean {
  return cartItems.some((item) => !item.item_status || item.item_status === "new");
}
